import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { Tabs, Tab } from 'react-bootstrap';
import * as Moment from "moment";
import { EcgAnalysisSetTest, TestNote, WebPortalClient, SessionNote, SessionAttachment } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { useReferenceContextProvider } from "../Core/ReferenceContextProvider";
import DocumentTitle from "../misc/components/DocumentTitle";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { LeanSessionMetadata, LeanSessionMetadataTable } from "../PulmonaryFunctionTests/Shared/SessionMetadataTable";
import { TestLeadAnnotations } from "./EcgAnalysisDetail";
import { EcgResultsPanel } from "./EcgResultsPanel";
import { EcgQTcTestParameters } from "./EcgTestAnnotations";
import { DeriveParametersFromAnnotations } from "./EcgCalculations";
import { LeadAnnotation } from "../misc/api/DeviceLinkApi/CalEcg";
import { EcgTestParametersComparison } from "./EcgTestParameters";
import { EcgCommentModals } from "./EcgCommentModals";
import { ToggleAlertFixedCommentCheckedAction, ToggleAlertFixedCommentsVisibilityAction, ToggleTestFixedCommentCheckedAction, ToggleTestFixedCommentsVisibilityAction, useFixedComments } from "./UseFixedComments";
import { SessionTypes } from "../misc/api/ApiSessionTypes";
import { InterpretationsSelector } from "./InterpretationsSelector";
import { SelectedCommentsCount } from "./EcgDashboard";
import { NotesModal } from "../misc/components/NotesModal";
import { useSecurityContext } from "../Core/SecurityContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../misc/api/WebPortalTokenClient";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { Authorization } from "../misc/components/Authorization";
import FileAttachmentsTable from "../file-attachments/components/viewer/FileAttachmentsTable";
import { FileAttachmentsTableMode } from "../file-attachments/models";

interface SessionLeadAnnotations {
    testId: string;
    testAnnotations: TestLeadAnnotations;
}

interface ResultsTabProps {
    t: EcgAnalysisSetTest;
    testAnnotations: LeadAnnotation[];
}

const ResultsTab = ({ t, testAnnotations }: ResultsTabProps) => {
    const client = useWebPortalApiClient();
    const [fixedCommentsState, fixedCommentsDispatch] = useFixedComments(t ? [t] : [], SessionTypes.Ecg);
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [testNotes, setTestNotes] = React.useState<TestNote[]>([]);
    const [notesVisible, setNotesVisible] = React.useState(false);
    const [isAddingNewNote, setIsAddingNewNote] = React.useState(false);

    React.useEffect(() => {
        const getComments = async () => {
            for (let i = 0; i < t.intepretations.length; i++) {
                fixedCommentsDispatch(ToggleTestFixedCommentCheckedAction.create(t.testId, t.intepretations[i].fixedCommentId, true));
            }

            for (let i = 0; i < t.alerts.length; i++) {
                fixedCommentsDispatch(ToggleAlertFixedCommentCheckedAction.create(t.testId, t.alerts[i].fixedCommentId, true));
            }
        }

        getComments();
    }, [t.intepretations.length, t.alerts.length, t.testId]);

    React.useEffect(() => {
        const getNotes = async () => {
            await fetchTestNotes(t.testId);
        }

        getNotes();
    }, [t.testId]);

    const fetchTestNotes = async (testId: string) => {
        const notesResult = await client.client.getTestNotes(undefined, `testId eq ${testId}`);
        setTestNotes(notesResult.value);
    }

    const addNewNote = async (comment: string) => {
        await client.client.postTestNote(
            {
                testId: t.testId,
                userEmailAddress: currentUser.Email,
                comment: comment,
                recorded: Moment().toISOString()
            })
        await fetchTestNotes(t.testId);
    }
    const editTestNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchTestNote(
            {
                comment: comment,
            }, id, annotation)
        await fetchTestNotes(t.testId);
    }

    if (fixedCommentsState.areAvailableFixedCommentsLoaded === null)
        return <LoadingWithMessage message="Loading ECG Recording..." />;

    const { rr, qt, qrs, pr } = DeriveParametersFromAnnotations(testAnnotations);

    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewEcgSessionNotes);

    return (
        <React.Fragment key={`ResultPanel${t.id}`}>
            <EcgResultsPanel test={t.test}>
                <div className="row">
                    <EcgQTcTestParameters qt={qt} rr={rr} siteTest={t.test} />
                    <EcgTestParametersComparison
                        oldQRS={t.test.qrs}
                        newQRS={qrs}
                        oldQT={t.test.qt}
                        newQT={qt}
                        oldPR={t.test.pr}
                        newPR={pr}
                        oldRR={t.test.rr}
                        newRR={rr}
                        oldP={t.test.p}
                        oldPQ={t.test.pq}
                        oldST={t.test.st}
                    />
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <InterpretationsSelector
                            testId={t.testId}
                            selectedInterpretationsCount={SelectedCommentsCount(fixedCommentsState.testFixedComments)}
                            onInterpretationClick={() => fixedCommentsDispatch(ToggleTestFixedCommentsVisibilityAction.create(t.testId))}
                            selectedAlertsCount={SelectedCommentsCount(fixedCommentsState.alertFixedComments)}
                            onAlertClick={() => fixedCommentsDispatch(ToggleAlertFixedCommentsVisibilityAction.create(t.testId))}
                        />&nbsp;

                        {hasViewNotePermission ? <button className="btn btn-primary" onClick={() => setNotesVisible(true)} data-automationid="TestNotesButton">
                            Notes&nbsp;
                            <span className="badge badge-info">{testNotes.length}</span>
                        </button> : null}
                    </div>
                </div>
            </EcgResultsPanel>

            <EcgCommentModals
                editable={false}
                testFixedComments={fixedCommentsState.testFixedComments}
                alertFixedComments={fixedCommentsState.alertFixedComments}
                dispatch={fixedCommentsDispatch}
            />

            <NotesModal
                isVisible={notesVisible}
                header="Recording Notes"
                isAddingNote={isAddingNewNote}
                notes={testNotes}
                onAdd={annotation => addNewNote(annotation)}
                // onEdit={(id, comment, annotation) => editTestNote(id, comment, annotation)}

                onCloseModal={() => setNotesVisible(false)}
                disabled={true}
            />
        </React.Fragment>
    );
}

export const EcgSiteView = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const client = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [sessionMetadata, setSessionMetadata] = React.useState<LeanSessionMetadata>();
    const { study } = useCurrentStudyConfigurationContext();
    const { overreadStatuses } = useReferenceContextProvider();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [analysisSetTests, setAnalysisSetTests] = React.useState<EcgAnalysisSetTest[]>();
    const [testAnnotations, setTestAnnotations] = React.useState<SessionLeadAnnotations[]>();
    const [sessionNotesVisible, setSessionNotesVisible] = React.useState(false);
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>([]);
    const [isAddingNewSessionNote, setIsAddingNewSessionNote] = React.useState(false);
    const [attachments, setAttachments] = React.useState<SessionAttachment[]>([]);

    React.useEffect(() => {
        const getData = async () => {
            const ecgAnalysisSetTestResults = await (await client.client.getEcgAnalysisSetTests(undefined, `analysisset/sessionId eq ${sessionId} and analysisset/type eq 'EAS_OVERREAD'`, undefined, "test,analysisset")).value;

            //const statusName = overreadStatuses.find(os => os.id === ecgAnalysisSetTestResult.analysisSet.status).name;

            setAnalysisSetTests(ecgAnalysisSetTestResults);

            const specificSession = await client.client.getEcgSession(sessionId, undefined, "visit($expand=subject($expand=site))");

            setSessionMetadata({
                Id: specificSession.id,
                SiteId: specificSession.visit.subject.site.id,
                SiteName: specificSession.visit.subject.site.name,
                SiteCountryName: specificSession.visit.subject.site.country,
                SiteNumber: specificSession.visit.subject.site.number,
                Technician: specificSession.technician,
                SubjectId: specificSession.visit.subject.id,
                SubjectNumber: specificSession.visit.subject.number,
                VisitName: specificSession.visit.name,
                SessionName: specificSession.name,
                SessionDateTime: specificSession.occurred,
                SessionTypeId: specificSession.sessionType,
                ReceivedDateTime: specificSession.created,
                SessionTypeName: specificSession.sessionType,
                Status: "",
                StatusId: "",
                Overreader: "",
                AnalysisSets: [],
                //StatusId: ecgAnalysisSetTestResult.analysisSet.status,
                //Overreader: ecgAnalysisSetTestResult.analysisSet.analyst,
                //AnalysisSets: [ecgAnalysisSetTestResult.analysisSet].map(a => ({
                //    Id: a.id,
                //    Analyst: {
                //        Username: a.analyst
                //    },
                //    Status: {
                //        Name: statusName
                //    }
                //}))
            });

            const attachmentsResults = await client.client.getSessionAttachments('id,size,name', `sessionId eq ${specificSession.id}`);
            setAttachments(attachmentsResults.value);

            setTestAnnotations(ecgAnalysisSetTestResults.map(t => ({
                testId: t.id,
                testAnnotations: {
                    ampsAnnotationsText: t.ampsAnnotationsTextBase64,
                    hl7AnnotationsText: t.hl7AnnotationsTextBase64,
                    annotations: t.annotations.map(a => ({
                        annotation: a.annotation,
                        leadIndex: a.leadIndex,
                        beatIndex: a.beatIndex,
                        value: a.value
                    })),
                }
            })));
        }

        getData();
    }, []);

    React.useEffect(() => {
        const getNotes = async () => {
            await fetchSessionNotes(sessionId);
        }

        getNotes();
    }, [sessionId]);

    const fetchSessionNotes = async (sessionId: string) => {
        const notesResult = await client.client.getSessionNotes(undefined, `sessionId eq ${sessionId}`);
        setSessionNotes(notesResult.value);
    }

    const addNewSessionNote = async (comment: string) => {
        await client.client.postSessionNote(
            {
                sessionId: sessionId,
                userEmailAddress: currentUser.Email,
                comment: comment,
                recorded: Moment().toISOString()
            })
        await fetchSessionNotes(sessionId);
    }

    const editSessionNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSessionNotes(sessionId);
    }

    if (!(sessionMetadata && analysisSetTests.length > 0))
        return <LoadingWithMessage message="Loading ECG Session..." />;

    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewEcgSessionNotes);

    return (
        <DocumentTitle title="ECG Recording | Vitalograph Web Portal">
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">ECG Session</h2>
                </div>
                <div className="h2 col-md-6">
                    <Link to="/sessions" className="pull-right btn btn-outline btn-primary">Back to List</Link>
                </div>
            </div>
            <LeanSessionMetadataTable
                sessionMetadata={sessionMetadata}
                selectedAnalysisSetId={analysisSetTests[0].analysisSetId}
            />

            <h2 className="text-primary">Results</h2>
            <Tabs defaultActiveKey={1} id="resultsTabs">
                {analysisSetTests.map(t => (<Tab key={t.test.index} eventKey={t.test.index} title={`Recording ${t.test.index}`}>
                    <ResultsTab t={t} testAnnotations={testAnnotations?.find(ta => ta.testId === t.id).testAnnotations?.annotations} />
                </Tab>))}
            </Tabs>

            <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.AccessEcgAnalysisSetsAttachment}>
                <h2 className="text-primary">Attachments</h2>
                <div className="panel panel-default">
                    <div className="panel-body">
                        <FileAttachmentsTable
                            legacyClient={legacyClient}
                            fileAttachments={attachments.map(a => ({ id: a.id, fileSizeInBytes: a.size, fileName: a.name }))}
                            mode={FileAttachmentsTableMode.ListOnly}
                        />
                    </div>
                </div>
            </Authorization>
            
            {hasViewNotePermission ? <button className="btn btn-primary" onClick={() => setSessionNotesVisible(true)} data-automationid="SessionNotesButton">
                Session Notes&nbsp;
                <span className="badge badge-info">{sessionNotes.length}</span>
            </button> : null}

            <NotesModal
                isVisible={sessionNotesVisible}
                header="Recording Notes"
                isAddingNote={isAddingNewSessionNote}
                notes={sessionNotes}
                onAdd={annotation => addNewSessionNote(annotation)}
                onEdit={(id, comment, annotation) => editSessionNote(id, comment, annotation)}

                onCloseModal={() => setSessionNotesVisible(false)}
                disabled={true}
            />

        </DocumentTitle>
    );
}
